// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { OctoChainData } from "./types";

export const SAMPLE_DATA: OctoChainData = {
  chain: "lidl_gb",
  chain_name: "Lidl GB",
  store_hours: { open: "06:00", close: "23:00" },
  last_updated: "2026-03-04",
  source: "04_lidl_rules_and_implementation.md (2026-03-04) — 26 rules",
  departments: [
    { code: "01", name: "Ambient", full: "01 - Ambient (1001)" },
    { code: "02", name: "F&V", full: "02 - Fruit and Veg (1002)" },
    { code: "03", name: "Chiller", full: "03 - Chiller (1003)" },
    { code: "04", name: "Bread", full: "04 - Bread (1004)" },
    { code: "05", name: "NF Consumables", full: "05 - NF Consumables (1005)" },
    { code: "06", name: "Freezer", full: "06 - Freezer (1006)" },
    { code: "07", name: "Meat & Poultry", full: "07 - Meat and Poultry (1007)" },
    { code: "08", name: "Chilled FV", full: "08 - Chilled FV (1008)" },
    { code: "09", name: "Bake Off", full: "09 - Bake Off (1009)" },
    { code: "12", name: "Plants & Flowers, but veryyyyy long line", full: "12 - Plants and Flowers (1012)" },
  ],
  task_types: [
    "OOS",
    "LOW",
    "FALSE_RESTOCK",
    "ZIN",
    "MBL",
    "SUSPICIOUS_STOCK",
    "Manual",
    "FLEX",
    "PSI",
    "DELISTED",
    "SCHEDULED_ROTATION",
    "Obstruction",
    "RESTOCK",
    "FACE_OVER",
    "PRESENTABILITY",
    "POG_RESET",
    "POG_COMPLIANCE",
    "TAG_COMPLIANCE",
  ],
  always_visible: [
    "OOS",
    "FALSE_RESTOCK",
    "MBL",
    "POG_RESET",
    "POG_COMPLIANCE",
    "TAG_COMPLIANCE",
    "SUSPICIOUS_STOCK",
    "SCHEDULED_ROTATION",
    "Obstruction",
    "Manual",
  ],
  always_visible_exclude: {
    FALSE_RESTOCK: ["02", "05"],
    MBL: ["02", "05", "07", "08", "09"],
  },
  globally_blocked_creation: ["RESTOCK", "FACE_OVER", "PRESENTABILITY"],
  rules: [
    {
      id: "C1",
      layer: "creation",
      task_type: "LOW",
      departments: ["02", "05"],
      windows: [],
      blocked: true,
      note: "No LOW for F&V and NF Consumables",
    },
    {
      id: "C2",
      layer: "creation",
      task_type: "ZIN",
      departments: ["05"],
      windows: [],
      blocked: true,
      note: "No ZIN for NF Consumables",
    },
    {
      id: "C3",
      layer: "creation",
      task_type: "MBL",
      departments: ["02", "05", "07", "08", "09"],
      windows: [],
      blocked: true,
      note: "MBL blocked outside Ambient/Freezer/Bread/Chiller",
    },
    {
      id: "C4",
      layer: "creation",
      task_type: "FLEX",
      departments: ["01", "03", "04", "06", "07", "08", "09"],
      windows: [],
      blocked: true,
      note: "FLEX only for F&V, NF Consumables, and Plants & Flowers",
    },
    {
      id: "C5",
      layer: "creation",
      task_type: "FLEX",
      departments: ["02", "05", "12"],
      windows: [{ start: "08:00", end: "09:00" }],
      blocked_outside_window: true,
      note: "FLEX creation only during 08:00-09:00",
    },
    {
      id: "C6",
      layer: "creation",
      task_type: "*",
      departments: ["*"],
      windows: [],
      blocked: true,
      note: "RESTOCK, FACE_OVER, PRESENTABILITY globally blocked",
      applies_to: ["RESTOCK", "FACE_OVER", "PRESENTABILITY"],
    },
    {
      id: "C7",
      layer: "creation",
      task_type: "LOW",
      departments: ["09"],
      windows: [],
      blocked: true,
      note: "No LOW for Bake Off — non-actionable due to bake schedules",
    },
    {
      id: "C8",
      layer: "creation",
      task_type: "*",
      departments: ["09"],
      windows: [{ start: "08:00", end: "20:00" }],
      blocked_outside_window: true,
      applies_to: ["OOS", "FALSE_RESTOCK", "ZIN"],
      note: "Bake Off OOS/FALSE_RESTOCK/ZIN only within +2h after open / -3h before close",
    },
    {
      id: "C9",
      layer: "creation",
      task_type: "*",
      departments: ["*"],
      windows: [{ start: "06:00", end: "22:50" }],
      blocked_outside_window: true,
      note: "Universal creation cutoff — no tasks within 10min of closing (22:50)",
    },
    {
      id: "W1",
      layer: "workability",
      task_type: "LOW",
      departments: ["01", "06", "08"],
      windows: [{ start: "06:00", end: "23:00" }],
      condition: "inventory.soh > (shc * 0.25 + 5)",
      note: "Ambient/Freezer/Chilled FV LOW workable only above 25% shelf capacity + 5",
    },
    {
      id: "W2",
      layer: "workability",
      task_type: "LOW",
      departments: ["03", "04", "07"],
      windows: [{ start: "06:00", end: "23:00" }],
      condition: "inventory.soh >= 8",
      note: "Chiller/Bread/Meat LOW workable only if SOH >= 8",
    },
    {
      id: "W3",
      layer: "workability",
      task_type: "*",
      departments: ["01", "02", "03", "04", "05", "06", "07", "08", "09", "12"],
      windows: [{ start: "06:00", end: "23:00" }],
      condition: "inventory.soh > 3",
      note: "All tasks non-workable if SOH <= 3 (immune: ZIN, PSI, Obstruction, Tag Compliance, Scheduled Rotation)",
    },
    {
      id: "V1",
      layer: "visibility",
      task_type: "LOW",
      departments: ["01", "06"],
      windows: [{ start: "08:00", end: "12:00", slot: "morning" }],
      note: "Ambient/Freezer LOW morning window",
    },
    {
      id: "V2",
      layer: "visibility",
      task_type: "LOW",
      departments: ["01", "06"],
      windows: [{ start: "16:00", end: "18:00", slot: "afternoon" }],
      note: "Ambient/Freezer LOW afternoon",
    },
    {
      id: "V3",
      layer: "visibility",
      task_type: "LOW",
      departments: ["03", "07", "04"],
      windows: [{ start: "19:00", end: "23:00", slot: "evening" }],
      note: "Chiller/Meat/Bread LOW evening window",
    },
    {
      id: "V4",
      layer: "visibility",
      task_type: "LOW",
      departments: ["01", "02", "03", "04", "05", "06", "07", "08", "09"],
      windows: [],
      blocked: true,
      note: "LOW default BLOCK — catch-all after V1-V3",
    },
    {
      id: "V5",
      layer: "visibility",
      task_type: "ZIN",
      departments: ["01", "02", "03", "04", "06", "07", "08", "09"],
      windows: [{ start: "08:30", end: "12:00", slot: "morning" }],
      note: "ZIN morning window (all depts except NF — blocked by C2)",
    },
    {
      id: "V6",
      layer: "visibility",
      task_type: "ZIN",
      departments: ["01", "02", "03", "04", "06", "07", "08", "09"],
      windows: [],
      blocked: true,
      note: "ZIN default BLOCK",
    },
    {
      id: "V9",
      layer: "visibility",
      task_type: "FLEX",
      departments: ["02", "05", "12"],
      windows: [{ start: "08:30", end: "12:00", slot: "morning" }],
      note: "FLEX morning window — F&V, NF, and Plants & Flowers",
    },
    {
      id: "V10",
      layer: "visibility",
      task_type: "FLEX",
      departments: ["02", "05", "12"],
      windows: [],
      blocked: true,
      note: "FLEX default BLOCK",
    },
    {
      id: "V11",
      layer: "visibility",
      task_type: "DELISTED",
      departments: ["01", "02", "03", "04", "06", "07", "08", "09"],
      windows: [{ start: "08:00", end: "12:00", slot: "morning" }],
      note: "DELISTED (Discontinued) morning window",
    },
    {
      id: "V12",
      layer: "visibility",
      task_type: "DELISTED",
      departments: ["01", "02", "03", "04", "06", "07", "08", "09"],
      windows: [],
      blocked: true,
      note: "DELISTED default BLOCK",
    },
    {
      id: "V13",
      layer: "visibility",
      task_type: "PSI",
      departments: ["01", "02", "03", "04", "06", "07", "08", "09"],
      windows: [{ start: "08:00", end: "12:00", slot: "morning" }],
      note: "PSI morning window",
    },
    {
      id: "V14",
      layer: "visibility",
      task_type: "PSI",
      departments: ["01", "02", "03", "04", "06", "07", "08", "09"],
      windows: [],
      blocked: true,
      note: "PSI default BLOCK",
    },
  ],
};
